/// <reference types="@types/google.maps" />

import { Injectable, NgZone } from '@angular/core';

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export type GoogleApiErrorType = 'timeout' | 'unavailable' | 'api_error';

export class GoogleApiError extends Error {
  constructor(
    public type: GoogleApiErrorType,
    public originalError?: any,
  ) {
    super(`Google API error: ${type}`);
    this.name = 'GoogleApiError';
  }
}

// ---------------------------------------------------------------------------
// Shared address data interfaces
// ---------------------------------------------------------------------------

/**
 * Structured address data returned by Google Places (and used throughout the
 * world-address flow).  The interface is the source of truth for both the
 * online (Google Places) and offline (ViaCEP / manual) paths.
 */
export interface PlaceAdditionalInfo {
  // ---- existing fields (kept for backward-compatibility) ------------------
  streetNumber: string;
  streetName: string;
  city: string;
  neighborhood: string;
  state: string;
  county: string;
  country: string;
  postalCode: string;
  buildingName: string;
  unitNumber: string;
  referencePoint: string;

  // ---- new fields ---------------------------------------------------------
  /** Decimal degrees latitude, null when resolution failed or is manual. */
  latitude: number | null;
  /** Decimal degrees longitude, null when resolution failed or is manual. */
  longitude: number | null;
  /**
   * How the address was resolved:
   * - `'google_places'` – user selected from Google autocomplete
   * - `'viacep'`        – offline mode, Brazilian CEP looked up via ViaCEP
   * - `'geocoding'`     – offline mode, lat/lng resolved via Google Geocoding
   * - `'manual'`        – offline mode, user filled everything by hand
   */
  resolvedVia: 'google_places' | 'viacep' | 'geocoding' | 'manual';
}

/** Default / empty PlaceAdditionalInfo value (useful for initialisation). */
export const EMPTY_PLACE_ADDITIONAL_INFO: PlaceAdditionalInfo = {
  streetNumber: '',
  streetName: '',
  city: '',
  neighborhood: '',
  state: '',
  county: '',
  country: '',
  postalCode: '',
  buildingName: '',
  unitNumber: '',
  referencePoint: '',
  latitude: null,
  longitude: null,
  resolvedVia: 'manual',
};

/** Result returned by {@link GooglePlacesService.getPlaceDetails}. */
export interface PlaceDetailsResult {
  additionalInfo: PlaceAdditionalInfo;
  formattedAddress: string | undefined;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable({ providedIn: 'root' })
export class GooglePlacesService {
  private autocompleteService: google.maps.places.AutocompleteService | null = null;
  private placesService: google.maps.places.PlacesService | null = null;
  private initialized = false;

  constructor(private zone: NgZone) {}

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Search for address predictions using Google Places Autocomplete.
   *
   * @throws {GoogleApiError} type `'unavailable'` – Google SDK not loaded
   * @throws {GoogleApiError} type `'timeout'`     – call took > 8 s
   * @throws {GoogleApiError} type `'api_error'`   – any other failure
   */
  async searchPredictions(
    input: string,
    countryRestriction?: string,
  ): Promise<google.maps.places.AutocompletePrediction[]> {
    if (!(await this.waitForSdk())) {
      throw new GoogleApiError('unavailable');
    }

    const apiCall = new Promise<google.maps.places.AutocompletePrediction[]>(
      (resolve, reject) => {
        try {
          this.autocompleteService!.getPlacePredictions(
            {
              input,
              ...(countryRestriction
                ? { componentRestrictions: { country: countryRestriction } }
                : {}),
            },
            (predictions, status) => {
              this.zone.run(() => {
                // CPK-4803: a REQUEST_DENIED / UNKNOWN_ERROR status used to be
                // swallowed as "no results", hiding SDK/key failures.
                if (
                  status !== google.maps.places.PlacesServiceStatus.OK &&
                  status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS
                ) {
                  console.warn('[GooglePlaces] predictions status:', status);
                  reject(new GoogleApiError('api_error', status));
                  return;
                }
                resolve(predictions && predictions.length > 0 ? predictions : []);
              });
            },
          );
        } catch (err) {
          reject(new GoogleApiError('api_error', err));
        }
      },
    );

    return this.race(apiCall, 8000);
  }

  /**
   * Fetch full details (address components + geometry) for a given place ID.
   *
   * @throws {GoogleApiError} type `'unavailable'` – Google SDK not loaded
   * @throws {GoogleApiError} type `'timeout'`     – call took > 8 s
   * @throws {GoogleApiError} type `'api_error'`   – any other failure
   */
  async getPlaceDetails(placeId: string): Promise<PlaceDetailsResult> {
    if (!(await this.waitForSdk())) {
      throw new GoogleApiError('unavailable');
    }

    const apiCall = new Promise<PlaceDetailsResult>((resolve, reject) => {
      try {
        this.placesService!.getDetails(
          {
            placeId,
            fields: ['address_components', 'geometry', 'formatted_address'],
          },
          (result) => {
            this.zone.run(() => {
              if (!result) {
                reject(new GoogleApiError('api_error'));
                return;
              }

              const additionalInfo = this.mapAddressComponents(
                result.address_components ?? [],
                result.geometry?.location ?? null,
              );

              resolve({
                additionalInfo,
                formattedAddress: result.formatted_address,
              });
            });
          },
        );
      } catch (err) {
        reject(new GoogleApiError('api_error', err));
      }
    });

    return this.race(apiCall, 8000);
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /**
   * CPK-4803: the Maps script is loaded `async` from index.html, so on slow
   * or older devices it can finish after the first search. Poll briefly for it
   * instead of failing with `unavailable` right away.
   */
  private async waitForSdk(maxWaitMs = 5000, stepMs = 250): Promise<boolean> {
    let waited = 0;
    while (!this.ensureInitialized()) {
      if (waited >= maxWaitMs) return false;
      await new Promise((r) => setTimeout(r, stepMs));
      waited += stepMs;
    }
    return true;
  }

  /**
   * Lazily initialise the Google Maps service instances.
   * Returns `false` when the SDK is not yet available.
   */
  private ensureInitialized(): boolean {
    if (this.initialized) return true;

    try {
      // Defensive check — SDK may not be loaded at component parse time
      if (
        typeof google === 'undefined' ||
        !google?.maps?.places
      ) {
        return false;
      }

      this.autocompleteService = new google.maps.places.AutocompleteService();
      this.placesService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );
      this.initialized = true;
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Returns a Promise that rejects with a `GoogleApiError('timeout')` after
   * `ms` milliseconds.
   */
  private createTimeout(ms: number): Promise<never> {
    return new Promise((_, reject) =>
      setTimeout(() => reject(new GoogleApiError('timeout')), ms),
    );
  }

  /**
   * Race an API call against a timeout.  Any non-`GoogleApiError` rejection
   * is wrapped into one.
   */
  private async race<T>(apiCall: Promise<T>, timeoutMs: number): Promise<T> {
    try {
      return await Promise.race([apiCall, this.createTimeout(timeoutMs)]);
    } catch (err) {
      if (err instanceof GoogleApiError) throw err;
      throw new GoogleApiError('api_error', err);
    }
  }

  /**
   * Map raw Google `address_components` (+ optional `LatLng`) into a
   * structured {@link PlaceAdditionalInfo} object.
   */
  private mapAddressComponents(
    addressComponents: google.maps.GeocoderAddressComponent[],
    location: google.maps.LatLng | null,
  ): PlaceAdditionalInfo {
    const result: PlaceAdditionalInfo = {
      ...EMPTY_PLACE_ADDITIONAL_INFO,
      latitude: location ? location.lat() : null,
      longitude: location ? location.lng() : null,
      resolvedVia: 'google_places',
    };

    for (const component of addressComponents) {
      for (const type of component.types) {
        switch (type) {
          case 'street_number':
            result.streetNumber = component.long_name;
            break;

          case 'route':
            result.streetName = component.long_name;
            break;

          // Prefer `locality`; fall back to `administrative_area_level_2`
          case 'locality':
            result.city = component.long_name;
            break;
          case 'administrative_area_level_2':
            if (!result.city) result.city = component.long_name;
            break;

          case 'sublocality':
          case 'sublocality_level_1':
          case 'sublocality_level_2':
          case 'sublocality_level_3':
          case 'sublocality_level_4':
          case 'sublocality_level_5':
            if (!result.neighborhood) result.neighborhood = component.long_name;
            break;

          case 'administrative_area_level_1':
            result.state = component.long_name;
            break;

          case 'country':
            result.country = component.long_name;
            break;

          case 'postal_code':
            result.postalCode = component.long_name;
            break;

          case 'premise':
            result.buildingName = component.long_name;
            break;

          case 'subpremise':
            result.unitNumber = component.long_name;
            break;

          default:
            break;
        }
      }
    }

    return result;
  }
}
